import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import SimpleLineupSection from './SimpleLineupSection';
import SimpleCreateArtistDealForm from './SimpleCreateArtistDealForm';
import SimpleEventDetailsCard from './SimpleEventDetailsCard';
// The pool's shared deal is the SAME form in both editors, so the Simple tab
// renders the normal editor's section rather than a re-skin of it — the two
// drifted apart when this was a separate component (O-2783 review).
import V3PoolDealSection from '../V3PoolDealSection';
import SimplePoolSplitCard from './SimplePoolSplitCard';
import { poolShareOfGross } from '../../../../shared/poolShareOfGross';

// `?edit=1` is set when the user picks "Edit" from a sibling's overflow menu,
// or when a brand-new simple offer is created from the new-offer modal; it
// tells the Deals tab to auto-open the deal form on load (edit mode, for the
// offer's primary performer) so creating/editing the deal is a single click.
const wantsEditOnLoad = () => {
  try {
    return new URLSearchParams(window.location.search).get('edit') === '1';
  } catch (e) {
    return false;
  }
};

const SimpleDealsTab = ({ ctrl }) => {
  const [dealForm, setDealForm] = useState(() => (wantsEditOnLoad() ? { mode: 'edit' } : null));

  // Strip the flag so a manual refresh doesn't reopen the form.
  useEffect(() => {
    if (!wantsEditOnLoad()) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('edit');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    } catch (e) {
      // history/URL unavailable — non-fatal.
    }
  }, []);

  // `poolActive` reads only the overlay's mode. The complex editor ANDs the
  // multi_artist_v3_offers entitlement into one `showPoolControls` covering all
  // its pool surfaces (V3OfferDealTermsPaneBody); do the same here rather than
  // gating one surface, or a team whose entitlement has lapsed can still move
  // the pool total in V3PoolDealSection — which is writable — while being unable
  // to see or fix who claims it.
  const poolActive = !!ctrl.team?.package?.multi_artist_v3_offers && ctrl.poolActive;
  const calc = ctrl.calculated || {};
  const poolMembers = calc.poolMembers || [];
  const poolAmount = calc.artistEarnings?.poolAmount || 0;
  const poolOversubscribed = calc.artistEarnings?.oversubscribed || false;

  return (
    <div className="simple-deals-main">
      {dealForm && (
        <SimpleCreateArtistDealForm
          ctrl={ctrl}
          mode={dealForm.mode}
          defaultRole={dealForm.role || 'headline'}
          onClose={() => setDealForm(null)}
        />
      )}
      <SimpleLineupSection
        ctrl={ctrl}
        onCreateDeal={() => setDealForm({ mode: 'create', role: 'headline' })}
        onAddSupport={() => setDealForm({ mode: 'create', role: 'support' })}
        onEdit={() => setDealForm({ mode: 'edit' })}
      />
      <SimpleEventDetailsCard ctrl={ctrl} />
      {poolActive && (
        <V3PoolDealSection
          pool={ctrl.offer?.v3_offer_pool}
          team={ctrl.team}
          poolSharePercent={poolShareOfGross(calc.artistEarnings)}
          dealBracketReached={calc.artistEarnings?.dealBracketReached || null}
        />
      )}
      {poolActive && (
        <SimplePoolSplitCard
          poolMembers={poolMembers}
          poolAmount={poolAmount}
          grossPotential={calc.artistEarnings?.grossPotential || 0}
          dealBracketReached={calc.artistEarnings?.dealBracketReached || null}
          oversubscribed={poolOversubscribed}
          unallocated={calc.artistEarnings?.unallocated || 0}
          lineup={ctrl.offer?.lineup || []}
          formatCalculatedNumber={ctrl.formatCalculatedNumber}
        />
      )}
    </div>
  );
};

SimpleDealsTab.propTypes = { ctrl: PropTypes.object.isRequired };

export default SimpleDealsTab;
